/**
 * ChapterSidebar — chapter list with reorder (native drag & drop), rename,
 * duplicate, and delete. The active chapter is highlighted.
 */

import React, { useState } from 'react';
import { Plus, GripVertical, MoreHorizontal, Copy, Trash2, Pencil } from 'lucide-react';
import { cx } from '@/components/site/primitives';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { countWords } from './utils/storyHelpers';

export default function ChapterSidebar({
  story, activeChapterId, onSelectChapter, onAddChapter, onRenameChapter,
  onDuplicateChapter, onDeleteChapter, onReorderChapters,
}) {
  const [renamingId, setRenamingId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [dragIndex, setDragIndex] = useState(null);

  const startRename = (chapter) => {
    setRenamingId(chapter.id);
    setDraftTitle(chapter.title);
  };

  const commitRename = (chapter) => {
    const trimmed = draftTitle.trim();
    if (trimmed && trimmed !== chapter.title) onRenameChapter(chapter.id, trimmed);
    setRenamingId(null);
  };

  return (
    <div className="flex h-full flex-col" data-testid="my-writing-chapter-sidebar">
      <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">
        {story.title}
      </p>

      <div className="mt-3 flex-1 space-y-0.5 overflow-y-auto">
        {story.chapters.map((chapter, index) => {
          const isActive = chapter.id === activeChapterId;
          const words = countWords(chapter.content);
          return (
            <div
              key={chapter.id}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (dragIndex !== null && dragIndex !== index) onReorderChapters(dragIndex, index);
                setDragIndex(null);
              }}
              className={cx(
                'group flex items-center gap-1.5 rounded-xl px-2 py-2 transition-colors',
                isActive ? 'bg-coral/10' : 'hover:bg-cream'
              )}
            >
              <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-subtle/50 opacity-0 group-hover:opacity-100" />
              <button
                type="button"
                onClick={() => onSelectChapter(chapter.id)}
                className="min-w-0 flex-1 text-left"
              >
                <span className={cx('block text-[11px] font-semibold', isActive ? 'text-coral' : 'text-subtle')}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                {renamingId === chapter.id ? (
                  <input
                    autoFocus
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={() => commitRename(chapter)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename(chapter);
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                    className="w-full rounded-md border border-coral/50 bg-cream px-1 py-0.5 text-sm text-ink outline-none"
                  />
                ) : (
                  <span className={cx('block truncate text-sm', isActive ? 'font-semibold text-ink' : 'text-ink/85')}>
                    {chapter.title || 'Untitled Chapter'}
                  </span>
                )}
                <span className="block text-[10px] text-subtle">{words.toLocaleString()} words</span>
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Chapter options"
                    className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-subtle opacity-0 transition-opacity hover:bg-card hover:text-ink group-hover:opacity-100"
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36 rounded-xl border-line bg-card text-ink">
                  <DropdownMenuItem onClick={() => startRename(chapter)}>
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDuplicateChapter(chapter.id)}>
                    <Copy className="mr-2 h-3.5 w-3.5" /> Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDeleteChapter(chapter.id)}
                    disabled={story.chapters.length <= 1}
                    className="text-coral focus:text-coral"
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onAddChapter}
        data-testid="my-writing-add-chapter"
        className="mt-2 flex items-center gap-1.5 rounded-xl px-2 py-2 text-sm font-medium text-subtle transition-colors hover:bg-cream hover:text-coral"
      >
        <Plus className="h-3.5 w-3.5" /> Add Chapter
      </button>
    </div>
  );
}
